import React, { useEffect } from 'react';
import { FadeIn } from '../components/ui/FadeIn';
import { SectionLabel } from '../components/ui/SectionLabel';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck } = FiIcons;

const benefits = [
  'A proven foundation of industry expertise in French bistro dining and fine dining operations.',
  'A focus on sustainable growth rooted in elevated bistro principles and modern hospitality strategy.',
  'A brand positioned to expand regionally and internationally, maintaining consistency and quality.',
  'A commitment to strong leadership, transparency, and profitability at every stage of growth.',
];

const tags = ['Timeless', 'For Investors & Partners', 'Muslim Owned French Bistro'];

export const BusinessPage = () => {
  useEffect(() => {
    const scripts = [
      'https://formnx.com/js/iframeResizer.js',
      'https://formnx.com/js/widget.js'
    ];

    const scriptElements = scripts.map(src => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
      return script;
    });

    return () => {
      scriptElements.forEach(script => {
        if (document.body.contains(script)) document.body.removeChild(script);
      });
    };
  }, []);

  return (
    <div className="pt-24 md:pt-32 bg-ivory min-h-screen">
      {/* Hero Header */}
      <section className="relative py-28 md:py-40 overflow-hidden bg-charcoal">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?q=80&w=2070&auto=format&fit=crop" alt="bg" className="w-full h-full object-cover" />
        </div>
        <div className="container mx-auto px-6 md:px-14 relative z-10 text-center">
          <FadeIn>
            <SectionLabel light>Investment & Partnership</SectionLabel>
            <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 leading-tight">
              Business Opportunity
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8" />
            <p className="text-white/60 font-light text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Partner with Maison De Charm to shape the future of Muslim Owned French bistro dining with fine dining excellence in Singapore and beyond.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-10">
              {tags.map((t) => (
                <span key={t} className="flex items-center gap-2 text-gold text-xs tracking-widest uppercase">
                  <SafeIcon icon={FiCheck} className="text-gold" />
                  {t}
                </span>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-28 md:py-36 bg-ivory">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <FadeIn className="mb-16">
            <SectionLabel>The Vision</SectionLabel>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal text-center mb-6">Why Partner with Us</h2>
            <div className="w-10 h-px bg-gold mx-auto mb-12" />
            <p className="text-charcoal/65 font-light leading-relaxed text-base md:text-lg text-center max-w-3xl mx-auto">
              Maison De Charm is more than a bistro — it is a vision of hospitality excellence built on experience, passion, and the belief that great food should be both approachable and extraordinary.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {benefits.map((b, i) => (
              <FadeIn key={i} delay={i * 0.1} direction="up">
                <div className="flex gap-5 p-8 border border-gold/15 bg-white shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-500">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center mt-0.5">
                    <SafeIcon icon={FiCheck} className="text-gold text-sm" />
                  </div>
                  <p className="text-charcoal/70 font-light text-sm leading-relaxed">{b}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.3}>
            <div className="bg-charcoal text-white p-10 md:p-16 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <p className="font-serif text-xl md:text-2xl text-gold/90 italic leading-relaxed max-w-3xl mx-auto relative z-10">
                "Partnering with Maison De Charm means joining a journey to redefine what a French bistro can be — warm and welcoming at heart, extraordinary in every detail."
              </p>
              <div className="mt-8 w-16 h-px bg-gold/40 mx-auto" />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-28 md:py-36 bg-ivory border-t border-gold/10">
        <div className="container mx-auto px-6 md:px-14 max-w-4xl">
          <FadeIn className="text-center mb-16">
            <SectionLabel>Get In Touch</SectionLabel>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal mb-5">Partner With Maison De Charm</h2>
            <div className="w-10 h-px bg-gold mx-auto mb-8" />
            <p className="text-charcoal/55 font-light text-sm leading-relaxed max-w-md mx-auto">
              We welcome conversations with investors, strategic partners, and like-minded visionaries who share our passion for exceptional bistro dining.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} direction="up">
            <div className="bg-white p-2 md:p-6 shadow-2xl rounded-sm border border-gold/5 overflow-hidden">
              <iframe 
                id="formnx-96pc18-d3gh5p7n" 
                src="https://fill.formnx.com/f/96pc18?iframe=true" 
                frameBorder="0" 
                style={{ width: '1px', minWidth: '100%', minHeight: '500px' }} 
                title="Maison De Charm Partnership Form"
              />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};